/**
 * BASE_URL is switchable between the R2 key layout served three ways:
 *  - fixtures: client/dev-fixtures/ via the Vite dev-server middleware
 *  - proxy:    client/dev-proxy/ (wrangler dev, R2 binding LIVE) on :8788
 *  - custom:   any URL (e.g. the production radar-live.terratile.io later)
 * Selection persists in localStorage; `?mode=` and `?base=` query params
 * override for one-off links (screenshots, sharing a repro).
 */

export type BaseUrlMode = "fixtures" | "proxy" | "production" | "custom";

const MODE_KEY = "rr.baseUrlMode";
const CUSTOM_KEY = "rr.baseUrlCustom";

const PRESETS: Record<"fixtures" | "proxy" | "production", string> = {
  fixtures: "/dev-fixtures",
  proxy: "http://localhost:8788",
  production: "https://radar-live.terratile.io",
};

function isMode(v: string | null): v is BaseUrlMode {
  return v === "fixtures" || v === "proxy" || v === "production" || v === "custom";
}

/** Dev-only modes can't work off localhost (fixtures aren't deployed; the proxy
 * is a local wrangler dev server). */
export function isDevHost(): boolean {
  return location.hostname === "localhost" || location.hostname === "127.0.0.1";
}

export function getMode(): BaseUrlMode {
  const q = new URLSearchParams(location.search).get("mode");
  if (isMode(q)) return q;
  const stored = localStorage.getItem(MODE_KEY);
  if (isMode(stored)) {
    // Self-heal: a persisted dev-only mode on a deployed host bricks all data
    // loading with no UI escape (live incident 2026-08-10). Clamp + repersist.
    if (!isDevHost() && (stored === "fixtures" || stored === "proxy")) {
      localStorage.setItem(MODE_KEY, "production");
      return "production";
    }
    return stored;
  }
  // Deployed site talks to production by default; local dev keeps fixtures.
  return isDevHost() ? "fixtures" : "production";
}

export function setMode(mode: BaseUrlMode): void {
  localStorage.setItem(MODE_KEY, mode);
}

export function getCustomUrl(): string {
  return localStorage.getItem(CUSTOM_KEY) ?? "";
}

export function setCustomUrl(url: string): void {
  localStorage.setItem(CUSTOM_KEY, url);
}

/** Resolved, trailing-slash-stripped base URL for the current mode. */
export function getBaseUrl(): string {
  const qBase = new URLSearchParams(location.search).get("base");
  if (qBase) return qBase.replace(/\/$/, "");
  const mode = getMode();
  if (mode === "custom") return getCustomUrl().replace(/\/$/, "");
  return PRESETS[mode];
}

export function manifestUrl(baseUrl: string, station: string): string {
  return `${baseUrl}/nexrad/${station}.json`;
}

export function frameUrl(baseUrl: string, station: string, ts: string): string {
  return `${baseUrl}/nexrad/${station}/${ts}.tif`;
}

/** Fleet manifest (workers/CONTRACTS.md "Fleet manifest — LIVE fleet.json"):
 * one aggregated {generatedMs, stations:[{id,lat,lon,latest}]} pointer, so
 * wide-zoom fusion can poll ONE url instead of N station manifests. Same
 * base URL/mode switching as everything else; poller-maintained, may not
 * exist yet in any given environment — fleetPoller.ts falls back to the
 * per-station manifests (already polled for loop/scrub history regardless)
 * when this 404s. */
export function fleetManifestUrl(baseUrl: string): string {
  return `${baseUrl}/fleet.json`;
}

/** Server-baked CONUS mosaic (workers/CONTRACTS.md "Mosaic-head" —
 * `mosaic/conus.json` hot manifest, max-age 5s, and `mosaic/conus/{ts}.tif`
 * immutable frame COGs) — the default composite (wide, multi-station) view.
 * Same base URL/mode switching as everything else. */
export function mosaicManifestUrl(baseUrl: string): string {
  return `${baseUrl}/mosaic/conus.json`;
}

export function mosaicFrameUrl(baseUrl: string, ts: string): string {
  return `${baseUrl}/mosaic/conus/${ts}.tif`;
}

export type CompositeRenderMode = "mosaic" | "fusion";

const RENDER_MODE_KEY = "rr.compositeRenderMode";

function isRenderMode(v: string | null): v is CompositeRenderMode {
  return v === "mosaic" || v === "fusion";
}

/** Which engine renders the composite (wide, multi-station) view: the
 * server-baked CONUS mosaic (default, works everywhere — mosaicController.ts)
 * or the client-side per-station WebGL fusion blend (fusionController.ts —
 * demoted to a dev-only research mode now that the mosaic is the mass-
 * audience default). Same persisted-mode + self-heal shape as getMode()
 * above, and for the same reason: a persisted "fusion" selection on a host
 * where the picker's dev-only option has since been stripped from the DOM
 * would otherwise have no UI escape hatch (the exact live incident getMode's
 * own self-heal comment describes, for the base-URL mode). */
export function getCompositeRenderMode(): CompositeRenderMode {
  const stored = localStorage.getItem(RENDER_MODE_KEY);
  if (isRenderMode(stored)) {
    if (!isDevHost() && stored === "fusion") {
      localStorage.setItem(RENDER_MODE_KEY, "mosaic");
      return "mosaic";
    }
    return stored;
  }
  return "mosaic";
}

export function setCompositeRenderMode(mode: CompositeRenderMode): void {
  localStorage.setItem(RENDER_MODE_KEY, mode);
}

const LAB_KEY = "rr.lab";

/** Visibility gate for the "Mosaic GL" smoothing-experiments panel
 * (app.ts's lab panel, mosaicGlController.ts): isDevHost() always qualifies;
 * a non-dev host qualifies only via `?lab=1` in the URL, sticky for the
 * REST OF THE BROWSER SESSION via sessionStorage. Deliberately NOT persisted
 * to localStorage like every other picker in this file — the self-heal
 * discipline here is applied by never writing to the durable store in the
 * first place, rather than by clamping a bad value after the fact
 * (getMode()/getCompositeRenderMode()'s approach): a `?lab=1` link shared
 * once (e.g. screenshotted, forwarded) must never silently turn into a
 * stuck default for a later, completely ordinary visit to the same host in
 * the same browser profile. */
export function isLabEnabled(): boolean {
  if (isDevHost()) return true;
  const q = new URLSearchParams(location.search).get("lab");
  if (q === "1") {
    sessionStorage.setItem(LAB_KEY, "1");
    return true;
  }
  return sessionStorage.getItem(LAB_KEY) === "1";
}
